import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

type Props = {
  fileName: string;
  getDocumentUrl: () => Promise<string | undefined>;
};

export function PdfPreview({ fileName, getDocumentUrl }: Props) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    setPreviewUrl(null);
    setError(null);

    const load = async () => {
      try {
        const url = await getDocumentUrl();
        if (controller.signal.aborted) return;
        if (!url) throw new Error("Dokumentet mangler en filadresse.");
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Kunne ikke hente PDF-en (HTTP ${response.status}).`);
        }
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        if (blob.size === 0) throw new Error("PDF-filen er tom.");
        objectUrl = URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" })
        );
        setPreviewUrl(objectUrl);
      } catch (cause) {
        if (!controller.signal.aborted) {
          setError(
            cause instanceof Error
              ? cause.message
              : "Kunne ikke laste forhåndsvisningen."
          );
        }
      }
    };
    void load();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileName, getDocumentUrl]);

  return (
    <div className="flex size-full min-h-0">
      {error ? (
        <div
          role="alert"
          className="m-auto p-6 text-center text-sm text-destructive"
        >
          Kunne ikke vise PDF: {error}
        </div>
      ) : previewUrl ? (
        <iframe
          src={previewUrl}
          title={`PDF-forhåndsvisning: ${fileName}`}
          className="size-full border-0"
          onError={() =>
            setError(
              "PDF-visningen kunne ikke åpnes. Prøv å laste ned dokumentet."
            )
          }
        />
      ) : (
        <div
          role="status"
          className="m-auto flex items-center gap-2 p-6 text-sm text-muted-foreground"
        >
          <Loader2 aria-hidden="true" className="size-5 animate-spin" />
          Laster PDF...
        </div>
      )}
    </div>
  );
}
